// External Libraries
import * as React from 'react';
import { Box, Button, Card, CardContent, CardHeader, Container, Grid, Link, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

// Icons
import {
  AccountBalanceOutlined, AttachMoneyOutlined, AutoFixHighOutlined, CalculateOutlined, CurrencyPoundOutlined, DynamicFeed,
  HourglassBottomOutlined, ImportantDevicesOutlined, MoreTimeOutlined,
  NotificationsActiveOutlined, QuizOutlined, SentimentSatisfiedOutlined, SettingsSuggestOutlined,
  Speed, WifiOff
} from '@mui/icons-material';

// Local components
import Subscribe from '../Subscribe';

// Hooks
import useLoggedIn from '../../hooks/useLoggedIn';
import useRegionDetection from '../../hooks/useRegionDetection';

// Define icon colors to cycle through
const iconColors = ['primary.main', 'secondary.main', 'success.main', 'info.main', 'warning.main', 'error.main'];

export default function Features() {
  const { loggedIn } = useLoggedIn();
  
  // Use the custom hook to detect if user is from the UK
  const isUserUK = useRegionDetection();
  
  // Display price based on user region
  const pricingText = isUserUK
    ? 'Yearly subscription for £29. Sign up for an account now, and create displays instantly'
    : 'Yearly subscription for $39. Sign up for an account now, and create displays instantly';

  // Map original cards to add icon color
  const cards = [
    { icon: <Typography variant='h3'>3,000+</Typography>, header: 'Examinations', text: 'Database with details of over 3,000 exam papers pre-loaded with duration, dates and names' },
    { icon: <QuizOutlined sx={{ fontSize: '50px' }} />, header: 'Leading qualifications', text: 'Leading qualifications fully supported, including IB DP, AP®, GCSE, IGCSE, AS/Level, International A Level' },
    { icon: <AccountBalanceOutlined sx={{ fontSize: '50px' }} />, header: 'Leading exam boards', text: 'All main exam boards supported, including IBO, CAIE, Pearson, AQA, OCR, College Board® (no affiliation with any board)' },
    { icon: <CalculateOutlined sx={{ fontSize: '50px' }} />, header: 'Start/Finish times', text: 'Display the correct start/finish times for all your exams, in a few clicks. Get it right. Every time' },
    { icon: <HourglassBottomOutlined sx={{ fontSize: '50px' }} />, header: 'Countdowns (can be turned off!)', text: 'Multiple countdowns for all your exams, including extra time. Students always know exactly how long they have left' },
    { icon: <MoreTimeOutlined sx={{ fontSize: '50px' }} />, header: 'Extra time', text: 'In one click, calculate and display extra time for all your exam papers (e.g. +25%, +50%), in real time. Including custom extra time.' },
    { icon: <NotificationsActiveOutlined sx={{ fontSize: '50px' }} />, header: 'Reminders', text: 'Automatically calculate and display remaining time reminders (5 minutes, 30 minutes)' },
    { icon: <DynamicFeed sx={{ fontSize: '50px' }} />, header: 'Multiple exams', text: 'Multiple exam papers in one invigilation room (multiple exam boards - no limits!)' },
    { icon: <AutoFixHighOutlined sx={{ fontSize: '50px' }} />, header: 'Fully editable', text: 'Edit duration, paper and subject names. Great for mock exams! ' },
    { icon: <WifiOff sx={{ fontSize: '50px' }} />, header: 'Works offline', text: 'Spotty Internet connection? ExamDisplay works even offline!' },
    { icon: isUserUK ? <CurrencyPoundOutlined sx={{ fontSize: '50px' }} /> : <AttachMoneyOutlined sx={{ fontSize: '50px' }} />, header: 'Great value', text: pricingText },
    { icon: <SentimentSatisfiedOutlined sx={{ fontSize: '50px' }} />, header: 'Easy to use', text: 'Created with and for invigilators. Train your invigilators in minutes.' },
    { icon: <SettingsSuggestOutlined sx={{ fontSize: '50px' }} />, header: 'Customisable', text: 'Zoom in/out, show/hide parts of your invigilation screens, choose colourful or high-contrast displays. The choice is yours ' },
    { icon: <ImportantDevicesOutlined sx={{ fontSize: '50px' }} />, header: 'Works on all devices', text: 'ExamDisplay works on all types of devices that support modern Web browsers, including iPad and Chromebook' },
    { icon: <Speed sx={{ fontSize: '50px' }} />, header: 'Built for speed', text: 'Built on the world\'s leading cloud infrastructure, enjoy a fast and smooth experience even during peak times' },
  ].map((card, index) => ({
    ...card,
    // Clone the icon element to add the color prop dynamically
    icon: React.cloneElement(card.icon, { sx: { ...card.icon.props.sx, color: iconColors[index % iconColors.length] } }),
  }));


  return (
    <React.Fragment>
      <Box
        sx={{
          bgcolor: 'background.default', // Use default background like Testimonials
          py: 6, // Match padding with Testimonials
          mb: 4, // Match margin with Hero
        }}
      >
        <Container maxWidth="lg" component="main"> {/* Ensure container is used */}
          <Typography
            component="h1"
            variant="h2"
            align="center"
            color="text.primary"
            gutterBottom
            sx={{
              fontSize: { // Corrected fontSize object
                lg: '3.5rem',
                md: '3rem',
                sm: '2.5rem',
                xs: '2rem'
              },
              mb: 5 // Increase margin below title
            }}
          >
            Powerful Features
          </Typography>
          {/* Removed redundant Container */}
          <Grid container spacing={4} alignItems="stretch"> {/* Use stretch alignment and adjust spacing */}
            {cards.map((card, index) => ( // Add index for key if header isn't unique, or use a better key
              // Adjust grid item sizing for better responsiveness if needed, keeping lg={4} for 3 columns
              <Grid item key={card.header + index} xs={12} sm={6} md={4}> {/* Use index in key for safety */}
                {/* Apply elevation and border for a modern card look */}
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 2, // Increase border radius for more rounded corners
                    border: (theme) => `1px solid ${theme.palette.grey[300]}`, // Lighter border
                    boxShadow: 0, // Remove base shadow, rely on border and hover
                    '&:hover': { // Add hover effect
                      boxShadow: 3, // Slightly increase shadow on hover
                      borderColor: 'primary.main', // Optional: highlight border on hover
                    },
                  }}
                >
                  {/* Center icon and adjust padding */}
                  <CardContent sx={{ display: 'flex', justifyContent: 'center', pt: 4, pb: 1 }}> {/* Increased top padding */}
                    {card.icon}
                  </CardContent>
                  {/* Adjust text alignment and padding */}
                  <CardContent sx={{ flexGrow: 1, textAlign: 'center', pt: 1 }}>
                    <Typography variant='h6' component="div" mb={1} sx={{ fontWeight: 600 }}>{card.header}</Typography> {/* Use h6, adjust weight */}
                    <Typography variant='body2' color="text.secondary">{card.text}</Typography> {/* Use body2 and secondary color */}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          {/* Center the Subscribe button if not logged in */}
          {!loggedIn && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
              <Subscribe color='success' size="large" />
            </Box>
          )}
        </Container>
      </Box>
    </React.Fragment>
  );
}
